import { Divider, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { Title } from '@repo/foundations';
import ActionCard from './ActionCard';
import { ACTION_TYPE } from './types';

interface IActionsListProps {
	onSelectAction: (actionType: ACTION_TYPE) => void;
}

function ActionsList({ onSelectAction }: IActionsListProps) {
	const theme = useMantineTheme();

	return (
		<Stack spacing={theme.spacing.xl} pt={theme.spacing.md}>
			<Stack spacing="xs">
				<Title size="md">Lineage</Title>
				<SimpleGrid cols={3} spacing="xl" verticalSpacing="xl">
					<ActionCard
						action={{
							label: 'Get Lineage',
							description:
								'Get a list of lineage based on the provided filters',
							emoji: '🔗',
							type: ACTION_TYPE.GET_LINEAGE,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_LINEAGE)}
					/>
					<ActionCard
						action={{
							label: 'Get Lineage Count',
							description:
								'Get the count of lineage based on the provided filters',
							emoji: '🧮',
							type: ACTION_TYPE.GET_LINEAGE_COUNT,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_LINEAGE_COUNT)}
					/>
					<ActionCard
						action={{
							label: 'Visualize Lineage Count',
							description:
								'Visualize the count of lineage based on the integration and native types of resources.',
							emoji: '📈',
							type: ACTION_TYPE.VISUALIZE_LINEAGE_COUNT,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.VISUALIZE_LINEAGE_COUNT)}
					/>
				</SimpleGrid>
			</Stack>
			<Divider />
			<Stack spacing="xs">
				<Title size="md">Resources</Title>
				<SimpleGrid cols={3} spacing="xl" verticalSpacing="xl">
					<ActionCard
						action={{
							label: 'Get Resource',
							description: 'Get a resource based on the provided filters',
							emoji: '📦',
							type: ACTION_TYPE.GET_RESOURCE,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_RESOURCE)}
					/>
					<ActionCard
						action={{
							label: 'Get Resource Count',
							description:
								'Get the count of resources based on the provided filters',
							emoji: '🧮',
							type: ACTION_TYPE.GET_RESOURCE_COUNT,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_RESOURCE_COUNT)}
					/>
					<ActionCard
						action={{
							label: 'Query Elasticsearch',
							description: 'Query the Elasticsearch index',
							emoji: '🔍',
							type: ACTION_TYPE.QUERY_ELASTICSEARCH,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.QUERY_ELASTICSEARCH)}
					/>
					<ActionCard
						action={{
							label: 'Download S3 File',
							description: 'Download a file from S3',
							emoji: '📥',
							type: ACTION_TYPE.DOWNLOAD_S3_FILE,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.DOWNLOAD_S3_FILE)}
					/>
					<ActionCard
						action={{
							label: 'Inspect Cache',
							description: 'Inspect value of a key from Redis',
							emoji: '💾',
							type: ACTION_TYPE.INSPECT_CACHE,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.INSPECT_CACHE)}
					/>
					<ActionCard
						action={{
							label: 'Clear Cache',
							description: 'Clear value of a cache from redis',
							emoji: '💔',
							type: ACTION_TYPE.DELETE_FROM_CACHE,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.DELETE_FROM_CACHE)}
					/>
					<ActionCard
						action={{
							label: 'Clear Query Parse Results',
							description:
								'Clear QueryParseResults for integration, optionally filtered by databuilder prefix',
							emoji: '🗑️',
							type: ACTION_TYPE.CLEAR_QUERY_PARSE_RESULTS,
						}}
						onClick={() =>
							onSelectAction(ACTION_TYPE.CLEAR_QUERY_PARSE_RESULTS)
						}
					/>
					<ActionCard
						action={{
							label: 'Regenerate Teams Permissions',
							description:
								'Start a background job to regenerate teams permissions.',
							emoji: '📤',
							type: ACTION_TYPE.REGENERATE_TEAMS_PERMISSIONS,
						}}
						onClick={() =>
							onSelectAction(ACTION_TYPE.REGENERATE_TEAMS_PERMISSIONS)
						}
					/>
					<ActionCard
						action={{
							label: 'Generate Workspace Lineage',
							description: 'Generate a workspace lineage graph',
							emoji: '📊',
							type: ACTION_TYPE.GENERATE_WORKSPACE_LINEAGE,
						}}
						onClick={() =>
							onSelectAction(ACTION_TYPE.GENERATE_WORKSPACE_LINEAGE)
						}
					/>
				</SimpleGrid>
			</Stack>
			<Divider />
			<Stack spacing="xs">
				<Title size="md">Users</Title>
				<SimpleGrid cols={3} spacing="xl" verticalSpacing="xl">
					<ActionCard
						action={{
							label: 'Get Workspace Users',
							description: 'Get a list of users in the workspace',
							emoji: '👨‍👩‍👧‍👦',
							type: ACTION_TYPE.GET_WORKSPACE_USERS,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_WORKSPACE_USERS)}
					/>
					<ActionCard
						action={{
							label: 'Get Workspace Admins',
							description: 'Get a list of admins in the workspace',
							emoji: '👮',
							type: ACTION_TYPE.GET_WORKSPACE_ADMINS,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_WORKSPACE_ADMINS)}
					/>
					<ActionCard
						action={{
							label: 'Remove Disabled Users From Teams',
							description:
								'Delete all team memberships for disabled users in a workspace',
							emoji: '🧹',
							type: ACTION_TYPE.REMOVE_DISABLED_USERS_FROM_TEAMS,
						}}
						onClick={() =>
							onSelectAction(ACTION_TYPE.REMOVE_DISABLED_USERS_FROM_TEAMS)
						}
					/>
				</SimpleGrid>
			</Stack>
			<Divider />
			<Stack spacing="xs">
				<Title size="md">Workspaces</Title>
				<SimpleGrid cols={3} spacing="xl" verticalSpacing="xl">
					<ActionCard
						action={{
							label: 'Get Workspace',
							description: 'Search for a workspace and get all its details',
							emoji: '🔍',
							type: ACTION_TYPE.GET_WORKSPACE,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.GET_WORKSPACE)}
					/>
					<ActionCard
						action={{
							label: 'Update Allowed Domains',
							description:
								'View, add, and remove allowed domains from a workspace',
							emoji: '🔒',
							type: ACTION_TYPE.UPDATE_ALLOWED_DOMAINS,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.UPDATE_ALLOWED_DOMAINS)}
					/>
					<ActionCard
						action={{
							label: 'Update Disabled',
							description: 'View and edit the disabled field of a workspace',
							emoji: '🚫',
							type: ACTION_TYPE.UPDATE_DISABLED,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.UPDATE_DISABLED)}
					/>
					<ActionCard
						action={{
							label: 'Regenerate Workspace Policies',
							description:
								'Regenerate all policies for a workspace (partial or complete rebuild)',
							emoji: '🔄',
							type: ACTION_TYPE.REGENERATE_WORKSPACE_POLICIES,
						}}
						onClick={() =>
							onSelectAction(ACTION_TYPE.REGENERATE_WORKSPACE_POLICIES)
						}
					/>
					<ActionCard
						action={{
							label: 'Update Workspace Quality Flags',
							description: 'Queue a refresh of quality flags for a workspace',
							emoji: '🚩',
							type: ACTION_TYPE.UPDATE_WORKSPACE_QUALITY_FLAGS,
						}}
						onClick={() =>
							onSelectAction(ACTION_TYPE.UPDATE_WORKSPACE_QUALITY_FLAGS)
						}
					/>
					<ActionCard
						action={{
							label: 'Reset DQS2 Policies',
							description:
								'Reset the DQS2 policies for a workspace using the superuser token',
							emoji: '🧹',
							type: ACTION_TYPE.RESET_DQS2_POLICIES,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.RESET_DQS2_POLICIES)}
					/>
					<ActionCard
						action={{
							label: 'Test Reverse Tunnel',
							description:
								'Test connectivity through a reverse tunnel (requires customer permission)',
							emoji: '🔌',
							type: ACTION_TYPE.TEST_REVERSE_TUNNEL,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.TEST_REVERSE_TUNNEL)}
					/>
					<ActionCard
						action={{
							label: 'Test Integration Connectivity',
							description:
								'Test integration connectivity with credential overrides',
							emoji: '🧪',
							type: ACTION_TYPE.TEST_CONNECTIVITY,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.TEST_CONNECTIVITY)}
					/>
					<ActionCard
						action={{
							label: 'Bulk Export',
							description:
								'Start a background job to export entities to CSV (emailed link).',
							emoji: '📤',
							type: ACTION_TYPE.BULK_EXPORT,
						}}
						onClick={() => onSelectAction(ACTION_TYPE.BULK_EXPORT)}
					/>
				</SimpleGrid>
			</Stack>
		</Stack>
	);
}

export default ActionsList;
